import { httpClient } from "./client";
import type { PlayerSearchScope, SocialApi } from "./interfaces";
import type {
  FriendshipResponse,
  FriendSummaryResponse,
  FriendRequestsResponse,
  FollowStatusResponse,
  BlockedUserResponse,
  BlockStatusResponse,
  PlayerCard,
  UserSearchResultResponse,
  ConversationResponse,
  DirectMessageAttachmentResponse,
  MessageCreate,
  MessageResponse,
  MessagePage,
  SocialActionResponse,
  UUID,
} from "../types";

export const socialApi: SocialApi = {
  sendFriendRequest: (toUserId: string) =>
    httpClient.post<FriendshipResponse>(
      `/social/friends/request?to_user_id=${encodeURIComponent(toUserId)}`,
    ),

  acceptFriendRequest: (friendshipId: UUID) =>
    httpClient.post<FriendshipResponse>(
      `/social/friends/${friendshipId}/accept`,
    ),

  declineFriendRequest: (friendshipId: UUID) =>
    httpClient.post<void>(`/social/friends/${friendshipId}/decline`),

  cancelFriendRequest: (friendshipId: UUID) =>
    httpClient.post<void>(`/social/friends/${friendshipId}/cancel`),

  unfriend: (otherUserId: string) =>
    httpClient.delete<void>(
      `/social/friends/${encodeURIComponent(otherUserId)}`,
    ),

  getFriendRequests: () =>
    httpClient.get<FriendRequestsResponse>("/social/friends/requests"),

  getFriends: () => httpClient.get<FriendSummaryResponse[]>("/social/friends"),

  searchUsers: (query: string, limit?: number) => {
    // Client-side blank guard: the server requires q (min_length=2) and 422s on
    // q="". A blank / whitespace-only query never hits the network — resolve [].
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      return Promise.resolve<UserSearchResultResponse[]>([]);
    }
    const sp = new URLSearchParams();
    sp.set("q", trimmed);
    if (limit !== undefined) sp.set("limit", String(limit));
    return httpClient.get<UserSearchResultResponse[]>(
      `/social/users/search?${sp.toString()}`,
    );
  },

  searchPlayers: (
    query: string,
    scope: PlayerSearchScope,
    opts?: { limit?: number },
  ) => {
    // Client-side blank guard: the server requires q (min_length=2) and 422s on
    // q="". A blank / whitespace-only query never hits the network — resolve [].
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      return Promise.resolve<PlayerCard[]>([]);
    }
    const sp = new URLSearchParams();
    sp.set("q", trimmed);
    sp.set("scope", scope);
    if (opts?.limit !== undefined) sp.set("limit", String(opts.limit));
    return httpClient.get<PlayerCard[]>(
      `/social/players/search?${sp.toString()}`,
    );
  },

  follow: (followeeId: string) =>
    httpClient.post<void>(`/social/follow/${encodeURIComponent(followeeId)}`),

  getFollowStatus: (followeeId: string) =>
    httpClient.get<FollowStatusResponse>(
      `/social/follow/${encodeURIComponent(followeeId)}`,
    ),

  unfollow: (followeeId: string) =>
    httpClient.delete<void>(`/social/follow/${encodeURIComponent(followeeId)}`),

  getFollowing: () =>
    httpClient.get<FriendSummaryResponse[]>("/social/following"),

  block: (blockedId: string) =>
    httpClient.post<void>(`/social/block/${encodeURIComponent(blockedId)}`),

  unblock: (blockedId: string) =>
    httpClient.delete<void>(`/social/block/${encodeURIComponent(blockedId)}`),

  getBlockedUsers: () =>
    httpClient.get<BlockedUserResponse[]>("/social/blocked"),

  getBlockStatus: (targetUserId: string) =>
    httpClient.get<BlockStatusResponse>(
      `/social/blocked/status?target_user_id=${encodeURIComponent(targetUserId)}`,
    ),

  createConversation: (participantIds: string[]) =>
    httpClient.post<ConversationResponse>("/social/conversations", {
      participant_ids: participantIds,
    }),

  listConversations: () =>
    httpClient.get<ConversationResponse[]>("/social/conversations"),

  sendMessage: (conversationId: UUID, data: MessageCreate) =>
    httpClient.post<MessageResponse>(
      `/social/conversations/${conversationId}/messages`,
      data,
    ),

  uploadMessageAttachment: (
    conversationId: UUID,
    file: File,
    idempotencyKey: UUID,
    onProgress: (percent: number) => void,
  ) => {
    const form = new FormData();
    form.set("file", file);
    form.set("idempotency_key", idempotencyKey);
    return httpClient.postFormWithProgress<DirectMessageAttachmentResponse>(
      `/social/conversations/${conversationId}/attachments`,
      form,
      onProgress,
    );
  },

  getMessages: (conversationId: UUID, params) => {
    const { signal, ...pagination } = params ?? {};
    return httpClient.get<MessagePage>(
      `/social/conversations/${conversationId}/messages?${new URLSearchParams({
        limit: String(pagination.limit ?? 50),
        ...(pagination.cursor ? { cursor: pagination.cursor } : {}),
      }).toString()}`,
      { signal },
    );
  },

  markConversationRead: (conversationId: UUID, messageIds: UUID[]) =>
    httpClient.post<SocialActionResponse>(
      `/social/conversations/${conversationId}/read`,
      { message_ids: messageIds },
    ),

  deleteMessage: (messageId: UUID) =>
    httpClient.delete<SocialActionResponse>(`/social/messages/${messageId}`),
};
